import { Link } from "react-router-dom";

export default function SimpleInterestVsCompound() {
    return (
        <>
            <p>
                Simple interest and compound interest produce the same result in year one. After that, they diverge — and the
                longer the time horizon, the wider the gap. Knowing which type applies to your loan or savings account changes
                how you evaluate a financial product and what strategies make sense to pay it off faster.
            </p>

            <h2>Simple Interest: The Basics</h2>
            <p>
                Simple interest is calculated only on the original principal. The formula is:
            </p>
            <p>
                Interest = Principal × Rate × Time
            </p>
            <p>
                You borrow $10,000 at 6% simple interest for 3 years:
            </p>
            <p>
                Interest = $10,000 × 0.06 × 3 = $1,800
            </p>
            <p>
                Total repaid = $11,800. The interest doesn't compound because it's always calculated against the original
                $10,000, not the growing balance.
            </p>
            <p>
                Most car loans use simple interest. That's important because it means extra principal payments reduce the
                balance on which future interest is calculated. If you pay an extra $500 in month 3, you're cutting future
                interest on that $500 for the entire remaining life of the loan.
            </p>

            <h2>Compound Interest: The Basics</h2>
            <p>
                Compound interest is calculated on the principal plus all previously accumulated interest. Each period's
                interest is added to the balance, and the next period's interest is calculated on the new, larger balance.
            </p>
            <p>
                Same example: $10,000 at 6% compounded annually for 3 years:
            </p>
            <p>
                Year 1: $10,000 × 0.06 = $600 interest. Balance: $10,600<br />
                Year 2: $10,600 × 0.06 = $636 interest. Balance: $11,236<br />
                Year 3: $11,236 × 0.06 = $674 interest. Balance: $11,910
            </p>
            <p>
                Total interest paid: $1,910 vs $1,800 with simple interest. A $110 difference on a $10,000 loan over 3 years.
                Not dramatic at short terms and low rates, but the gap grows exponentially with time.
            </p>
            <p>
                Use the <Link to="/calculator/simple-interest">simple interest calculator</Link> to model your specific loan
                amount, rate, and term.
            </p>

            <h2>Where Each Type Applies</h2>
            <p>
                <strong>Simple interest loans:</strong> Auto loans, some personal loans, most mortgage products (though
                mortgage amortization is its own category).
            </p>
            <p>
                <strong>Compound interest:</strong> Credit cards (compounding daily), savings accounts, CDs, money market
                accounts, investments, student loans (in most cases), and mortgages from the lender's perspective.
            </p>
            <p>
                The critical distinction: credit cards compound interest daily on any unpaid balance. That's why they're so
                expensive. A $5,000 balance at 21% APR compounding daily accrues $2.88 per day in interest on day one, and
                the next day's interest is calculated on $5,002.88. Over time, this snowballs into the numbers that make
                minimum payments nearly useless.
            </p>

            <h2>The Real-World Gap Over Time</h2>
            <p>
                On savings, compound interest is your ally. On debt, simple interest is your friend. Here's a comparison
                across time horizons at 6%:
            </p>
            <p>
                <strong>$10,000 invested for 20 years:</strong><br />
                Simple interest: $10,000 + ($10,000 × 0.06 × 20) = $22,000<br />
                Compound interest (annual): $10,000 × (1.06)^20 = $32,071
            </p>
            <p>
                The compound investor ends up with $10,071 more — from the same initial amount at the same rate, purely
                because of how the interest is calculated.
            </p>
            <p>
                <strong>$10,000 borrowed for 20 years at 6%:</strong><br />
                Simple interest total owed: $22,000<br />
                Compound interest total owed: $32,071
            </p>
            <p>
                As a borrower, you'd rather owe $22,000 than $32,071. This is why it matters which type of interest your loan
                uses — especially for long-term debt.
            </p>

            <h2>Strategies That Work Differently on Each Type</h2>
            <p>
                <strong>Extra payments on a simple interest loan:</strong> Highly effective. Because interest is calculated
                on the outstanding principal, reducing that principal early cuts all future interest charges proportionally.
                On a 5-year auto loan at 6%, an extra $100/month payment can save you hundreds in interest and shorten the
                term by several months.
            </p>
            <p>
                <strong>Extra payments on a compound interest loan:</strong> Also effective, but the mechanism is different.
                On a credit card (daily compounding), paying more than the minimum immediately reduces the balance on which
                tomorrow's interest is calculated. Pay it down as fast as possible — every day's balance reduction saves
                compounded interest.
            </p>
            <p>
                <strong>Timing of payments:</strong> For simple interest loans, paying early in the billing cycle reduces
                interest for that period. For compound interest debt like credit cards, pay as early and as often as possible.
            </p>

            <h2>How to Tell Which Type Your Loan Is</h2>
            <p>
                Read your loan agreement or promissory note. It will specify. If it says "interest accrues daily on the
                unpaid principal balance," it's simple interest. If it says something about compounding periods or an effective
                annual rate (EAR) that differs from the stated APR, it's compounding. For credit cards, it's always daily
                compounding — that's how they work.
            </p>
            <p>
                You can also ask your lender directly. Knowing the answer changes what payoff strategies make sense.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                Simple interest grows linearly. Compound interest grows exponentially. As an investor, compound interest is
                the engine of long-term wealth. As a borrower, it's what makes high-rate debt so expensive to carry. Know
                which one you're dealing with, and use that knowledge to your advantage. The{" "}
                <Link to="/calculator/simple-interest">simple interest calculator</Link> lets you model any loan scenario
                quickly to compare total cost at different rates and terms.
            </p>
        </>
    );
}